import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { CateringCartService } from '../../../../core/services/catering-cart.service';
import { BookMeetingService } from '../../../../core/services/book-meeting.service';
import { CateringVendorService } from '../../../../core/services/catering-vendor.service';
import { PaperButtonComponent } from '@ricoh-paper/angular';
import { DeliveryTimePickerComponent } from '../delivery-time-picker/delivery-time-picker.component';

/**
 * EmailRequestFormComponent — free-text form for EMAIL_ONLY vendors.
 *
 * Instead of browsing a menu and adding items to a cart, the user
 * describes their catering requirements in free text, sets a headcount,
 * chooses a delivery time, and optionally adds delivery instructions.
 * On submit, the draft is attached to the meeting via BookMeetingService.
 */
@Component({
  selector: 'ct-email-request-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DeliveryTimePickerComponent, PaperButtonComponent, TranslocoPipe],
  template: `
    <div class="erf">
      <!-- Vendor description -->
      @if (vendorService.selectedVendor()?.description; as desc) {
        <p class="erf__vendor-desc">{{ desc }}</p>
      }

      <!-- Free text request -->
      <div class="erf__field">
        <label class="erf__label">{{ 'catering.customer.email_request.what_to_order' | transloco }}</label>
        <textarea
          class="erf__textarea ph-mask"
          rows="6"
          [placeholder]="'catering.customer.email_request.describe_placeholder' | transloco"
          [value]="cartService.freeTextRequest()"
          (input)="cartService.setFreeTextRequest($any($event.target).value)"></textarea>
      </div>

      <!-- Number of people -->
      <div class="erf__field">
        <label class="erf__label">{{ 'catering.customer.email_request.number_of_people' | transloco }}</label>
        <input type="number" class="erf__input" min="1" [placeholder]="'catering.customer.email_request.people_placeholder' | transloco"
               (input)="onPeopleCount($any($event.target).value)" />
      </div>

      <!-- Delivery time -->
      <ct-delivery-time-picker
        [value]="cartService.deliveryTime()"
        [meetingStart]="meetingService.startTime()"
        [meetingEnd]="meetingService.endTime()"
        [vendorOpenTime]="vendorOpenTime()"
        [vendorCloseTime]="vendorCloseTime()"
        (timeChanged)="cartService.setDeliveryTime($event)" />

      <!-- Notes -->
      <div class="erf__field">
        <label class="erf__label">{{ 'catering.customer.email_request.delivery_instructions' | transloco }}</label>
        <textarea
          class="erf__textarea ph-mask"
          rows="3"
          [placeholder]="'catering.customer.email_request.instructions_placeholder' | transloco"
          [value]="cartService.notes()"
          (input)="cartService.setNotes($any($event.target).value)"></textarea>
      </div>

      <!-- Submit -->
      <div class="erf__footer">
        <paper-button intent="primary" size="md" [block]="true" [disabled]="!canSubmit()" (click)="onSendRequest()">
          {{ 'catering.customer.email_request.send_request' | transloco }}
        </paper-button>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }

    .erf {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .erf__vendor-desc {
      margin: 0;
      font-size: 12px;
      font-style: italic;
      color: var(--text-secondary);
      line-height: 1.5;
    }

    .erf__field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .erf__label {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--text-tertiary);
    }

    .erf__textarea {
      width: 100%;
      padding: 8px 12px;
      font-size: 12px;
      font-family: inherit;
      color: var(--text);
      background: var(--surface-2);
      border: 1px solid var(--border-low);
      border-radius: var(--radius-sm);
      outline: none;
      resize: vertical;
      transition: border-color 0.15s ease;

      &::placeholder { color: var(--text-tertiary); }
      &:focus { border-color: var(--color-primary); }
    }

    .erf__input {
      width: 100%;
      padding: 8px 12px;
      font-size: 12px;
      font-family: inherit;
      color: var(--text);
      background: var(--surface-2);
      border: 1px solid var(--border-low);
      border-radius: var(--radius-sm);
      outline: none;
      transition: border-color 0.15s ease;

      &::placeholder { color: var(--text-tertiary); }
      &:focus { border-color: var(--color-primary); }
    }

    .erf__footer {
      padding-top: 8px;
    }

    @media (prefers-reduced-motion: reduce) {
      .erf__textarea,
      .erf__input {
        transition: none;
      }
    }
  `],
})
export class EmailRequestFormComponent {
  protected readonly cartService = inject(CateringCartService);
  protected readonly meetingService = inject(BookMeetingService);
  protected readonly vendorService = inject(CateringVendorService);

  /** Vendor open time for today (from operating hours) */
  protected readonly vendorOpenTime = computed(() => {
    const vendor = this.vendorService.selectedVendor();
    if (!vendor?.operatingHours?.length) return null;
    const day = new Date().getDay();
    const hours = vendor.operatingHours.find(h => h.dayOfWeek === day);
    return hours?.open ?? null;
  });

  /** Vendor close time for today (from operating hours) */
  protected readonly vendorCloseTime = computed(() => {
    const vendor = this.vendorService.selectedVendor();
    if (!vendor?.operatingHours?.length) return null;
    const day = new Date().getDay();
    const hours = vendor.operatingHours.find(h => h.dayOfWeek === day);
    return hours?.close ?? null;
  });

  /** Can submit: free text is non-empty AND delivery time is set */
  protected readonly canSubmit = computed(() =>
    !!this.cartService.freeTextRequest()?.trim() && this.cartService.deliveryTime() !== null,
  );

  /** Handle people count input — stored as a dedicated headcount signal */
  protected onPeopleCount(value: string): void {
    const count = parseInt(value, 10);
    if (!isNaN(count) && count > 0) {
      this.cartService.setHeadcount(count);
    }
  }

  /** Submit the email request as a draft attached to the meeting */
  protected onSendRequest(): void {
    const room = this.meetingService.activeRoomForCatering();
    if (!room) return;

    const dayIndex = this.meetingService.isMultiDay() ? this.meetingService.activeDayIndex() : undefined;
    const draft = this.cartService.toDraft(room.spaceId, room.name, dayIndex);
    this.meetingService.addCateringOrder(draft);
    this.cartService.reset();
    this.meetingService.backToForm();
  }
}
